/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonPage,
  IonToolbar,
  IonItem,
  IonButton,
  IonLoading,
  IonCheckbox,
  IonImg,
} from '@ionic/react';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import './Connect.scss';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { AppDispatch } from '../../store/store';
import { connect, disconnect, pair } from '../../store/reducers/bleReducer';
import { RouteComponentProps } from 'react-router';
import { CheckboxChangeEventDetail } from '@ionic/core';

import logoSvg from '../../assets/logo.svg';
import { sleep } from '../../helpers/sleep';

const version = require('../../../package.json').version;

const Connect: React.FC<RouteComponentProps> = ({ history }: RouteComponentProps) => {
  const [pairingMode, setPairingMode] = useState(false);
  // const [allDevices, setAllDevices] = useState(false);
  const cancel = useRef(false);
  const [loadingMessage, setLoadingMessage] = useState('Connecting ...');
  const connectAttempts = useSelector((state: RootState) => state.ble.connectAttempts);
  const connected = useSelector((state: RootState) => state.ble.connected);
  const error = useSelector((state: RootState) => state.ble.error);
  const connecting = useSelector((state: RootState) => state.ble.connecting);
  const hasKey = useSelector((state: RootState) => state.ble.authKey !== '');
  const hasConnected = useSelector((state: RootState) => state.ble.hasConnected);

  const getInfo = (mode = pairingMode): JSX.Element => {
    if (hasKey && !mode) {
      return (
        <div>
          <p>Welcome to the Ghost MV1 web app.</p>
          <p>
            A vape has previously been connected; to reconnect, simply make sure your vape is in standby mode or powered
            on.
          </p>
        </div>
      );
    } else {
      return (
        <div>
          <p>Welcome to the Ghost MV1 web app.</p>
          <p>
            Make sure your vape is powered on and and in sleep mode (at this point there should be no LEDs illuminated).
          </p>
          <p>
            Hold down both buttons on the MV1 until the green LEDs flash from side-to-side. Your vape is now in pairing
            mode.
          </p>
        </div>
      );
    }
  };

  const [info, setInfo] = useState(getInfo());
  const dispatch = useDispatch<AppDispatch>();

  const pairBLE = async (): Promise<void> => {
    cancel.current = false;
    updateLoadingMessage(1);
    const result = await dispatch(pair(false));
    if (result) {
      history.push('/dashboard');
    }
  };

  const getCancelCallback = useCallback((): boolean => {
    return cancel.current;
  }, [cancel]);

  const connectBLE = async (): Promise<void> => {
    cancel.current = false;
    let attempts = 0;
    updateLoadingMessage(attempts + 1);
    while (!getCancelCallback() && attempts >= 0) {
      attempts = (await dispatch(connect(false))) as number;
      if (attempts >= 0) {
        if (attempts >= 5) {
          cancel.current = true;
          setInfo(
            <div>
              <p>We are having trouble reconnecting to your vape. Please try re-pairing the device.</p>
              <p>
                Make sure your vape is powered on and and in sleep mode (at this point there should be no LEDs
                illuminated).
              </p>
              <p>
                Hold down both buttons on the MV1 until the green LEDs flash from side-to-side. Your vape is now in
                pairing mode.
              </p>
            </div>,
          );
          setPairingMode(true);
          dispatch(disconnect());
        } else {
          updateLoadingMessage(attempts + 1);
          await sleep(2500);
        }
      } else if (attempts === -2) {
        cancel.current = true;
        setInfo(
          <div>
            <p>Your vape has been paired with another device. You will need to re-pair it with this one to continue.</p>
            <p>
              Make sure your vape is powered on and and in sleep mode (at this point there should be no LEDs
              illuminated).
            </p>
            <p>
              Hold down both buttons on the MV1 until the green LEDs flash from side-to-side. Your vape is now in
              pairing mode.
            </p>
          </div>,
        );
        setPairingMode(true);
        dispatch(disconnect());
      } else if (!getCancelCallback()) {
        history.push('/dashboard');
      }
    }
  };

  const updateLoadingMessage = useCallback(
    (attempts: number) => {
      const newMessage = attempts > 1 ? `Connecting (attempt #${attempts}) ...` : 'Connecting ...';
      if (newMessage !== loadingMessage) {
        setLoadingMessage(newMessage);
        const elem = document.querySelector('div.loading-wrapper div.loading-content');
        if (elem) {
          elem.innerHTML = newMessage;
        }
      }
    },
    [loadingMessage],
  );

  const pairingModeChange = (event: CustomEvent<CheckboxChangeEventDetail>): void => {
    setPairingMode(event.detail.checked);
    setInfo(getInfo(event.detail.checked));
  };

  // const allDevicesChange = (event: CustomEvent<CheckboxChangeEventDetail>): void => {
  //     setAllDevices(event.detail.checked);
  // };

  useEffect(() => {
    if (!connected && hasConnected) {
      dispatch(disconnect());
      setInfo(
        <div>
          <p>Connection to your vape was lost.</p>
          <p>To reconnect, simply make sure your vape is in standby mode or powered on.</p>
        </div>,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, hasConnected]);

  return (
    <IonPage className="connect">
      <IonLabel class="version-label">{version}</IonLabel>
      <IonHeader className="connect-header">
        <IonToolbar className="connect-header-toolbar">
          <IonImg className="connect-header-toolbar-logo" src={logoSvg} />
        </IonToolbar>
      </IonHeader>
      <IonContent className="connect-content" scrollX={false} scrollY={false}>
        <IonList lines="none" className="connect-content-list">
          {/* <IonListHeader className="connect-content-list-header">
            <IonLabel>Connect</IonLabel>
          </IonListHeader> */}
          <IonItem class="connect-content-list-spacer" />
          <IonItem class="connect-info" hidden={error !== undefined || connecting || connected}>
            <IonHeader>{info}</IonHeader>
          </IonItem>
          <IonItem
            className="connect-content-list-button"
            hidden={error !== undefined || hasKey || connecting || connected}
          >
            <IonButton onClick={pairBLE} size="large">
              Pair
            </IonButton>
          </IonItem>
          <IonItem
            className="connect-content-list-button"
            hidden={error !== undefined || connecting || connected || !hasKey}
          >
            <IonButton
              onClick={
                pairingMode
                  ? (): void => {
                      pairBLE();
                    }
                  : (): void => {
                      connectBLE();
                    }
              }
              size="large"
            >
              {pairingMode ? 'Pair' : 'Connect'}
            </IonButton>
          </IonItem>
          <IonItem
            className="connect-content-list-options"
            hidden={error !== undefined || connecting || connected || !hasKey}
          >
            <IonCheckbox checked={pairingMode} onIonChange={pairingModeChange} slot="start" />
            <IonLabel>Pair device again?</IonLabel>
          </IonItem>
          {/* <IonItem
                        className="connect-content-list-options"
                        hidden={error !== undefined || connecting || connected || !hasKey}
                    >
                        <IonCheckbox defaultChecked={allDevices} onIonChange={allDevicesChange} slot="start" />
                        <IonLabel>Show all devices?</IonLabel>
                    </IonItem> */}
          <IonItem class="connect-content-list-spacer" />
        </IonList>
      </IonContent>
      <IonLoading
        isOpen={connecting || connectAttempts > 0}
        message={loadingMessage}
        backdropDismiss={true}
        keyboardClose={true}
        onDidDismiss={(): void => {
          if (!connected) {
            cancel.current = true;
            dispatch(disconnect());
          }
        }}
      />
    </IonPage>
  );
};

export default Connect;
